import { useMemo, useState } from "react";
import {
  Alert,
  Box,
  IconButton,
  InputAdornment,
  Skeleton,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";

import { SideDrawer } from "../../../components/SideDrawer";
import {
  ACCENT,
  CANVAS,
  FONT,
  HAIRLINE,
  ICON,
  INK_MUTED,
  INK_SECONDARY,
  RADIUS,
  SURFACE,
} from "../../../theme/tokens";
import MainPanelContent, {
  CONTENT_MAX_WIDTH,
} from "../components/MainPanelContent";

import ArtifactCard from "./components/ArtifactCard";
import ArtifactDetail from "./components/ArtifactDetail";
import GitLabFilePreview from "./components/GitLabFilePreview";

import { useGitLabProjects } from "../../../hooks/useGitLabProjects";

const GRID_SX = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 320px))",
  gap: 2,
};

const TABS = [
  { label: "Base Images", subgroup: "efabric/base-images" },
  { label: "Experiments", subgroup: "efabric/experiments" },
];

// Base Images are about the image itself; Templates/Experiments are about
// their files — so the detail view opens on the most relevant tab.
function defaultTabForSubgroup(subgroup) {
  return subgroup === "efabric/base-images" ? 0 : 1;
}

export default function WorkflowCatalog() {
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const currentTab = TABS[activeTab];
  const { projects, loading, error } = useGitLabProjects(currentTab.subgroup);

  const handleTabChange = (_e, newValue) => {
    setActiveTab(newValue);
    setSearch("");
    setSelectedProject(null);
    setSelectedFile(null);
  };

  const handleSelectProject = (project) => {
    setSelectedProject(project);
    setSelectedFile(null);
  };

  const handleBack = () => {
    setSelectedProject(null);
    setSelectedFile(null);
  };

  const filteredProjects = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => {
      const name = (p.name || "").toLowerCase();
      const desc = (p.description || "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [projects, search]);

  return (
    <MainPanelContent
      title="Workflow Templates"
      subtitle={
        "Browse base images, workflow templates, and experiments. Each repository " +
        "bundles the container images and files you need to reproduce your work."
      }
      maxWidth={CONTENT_MAX_WIDTH}
      contentGap={0}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          minHeight: 0,
          overflow: "hidden",
          backgroundColor: CANVAS,
        }}
      >
        {/* Scroll region */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            pb: { xs: 3, sm: 4 },
          }}
        >
          {selectedProject ? (
            <ArtifactDetail
              key={selectedProject.id}
              project={selectedProject}
              categoryLabel={currentTab.label}
              defaultTab={defaultTabForSubgroup(currentTab.subgroup)}
              onBack={handleBack}
              onOpenFile={setSelectedFile}
            />
          ) : (
            <>
              {/* Category tabs */}
              <Box sx={{ borderBottom: `1px solid ${HAIRLINE}`, mb: 2 }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  sx={{
                    minHeight: 44,
                    "& .MuiTab-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: FONT.sm,
                      color: INK_SECONDARY,
                      minHeight: 44,
                    },
                    "& .Mui-selected": { color: ACCENT },
                    "& .MuiTabs-indicator": {
                      backgroundColor: ACCENT,
                      height: 2,
                    },
                  }}
                >
                  {TABS.map((t) => (
                    <Tab key={t.subgroup} label={t.label} />
                  ))}
                </Tabs>
              </Box>

              {/* Search */}
              <TextField
                fullWidth
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${currentTab.label.toLowerCase()} by name or description…`}
                sx={{
                  mb: 2.5,
                  maxWidth: 480,
                  "& .MuiOutlinedInput-root": {
                    backgroundColor: SURFACE,
                    borderRadius: `${RADIUS.sm}px`,
                    fontSize: FONT.md,
                  },
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: ICON }} />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setSearch("")}
                          sx={{ color: ICON }}
                        >
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />

              {/* Grid / states */}
              {error ? (
                <Alert severity="error">{error}</Alert>
              ) : loading ? (
                <Box sx={GRID_SX}>
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton
                      key={i}
                      variant="rounded"
                      height={148}
                      sx={{ borderRadius: `${RADIUS.md}px` }}
                    />
                  ))}
                </Box>
              ) : filteredProjects.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ fontSize: FONT.md, color: INK_SECONDARY }}>
                    {search
                      ? "No repositories match your search."
                      : "No repositories found."}
                  </Typography>
                </Box>
              ) : (
                <Box sx={GRID_SX}>
                  {filteredProjects.map((project) => (
                    <ArtifactCard
                      key={project.id}
                      project={project}
                      onSelect={handleSelectProject}
                    />
                  ))}
                </Box>
              )}

              {!loading && !error && filteredProjects.length > 0 && (
                <Typography sx={{ fontSize: FONT.xs, color: INK_MUTED, mt: 2 }}>
                  {filteredProjects.length} of {projects.length}{" "}
                  {projects.length === 1 ? "repository" : "repositories"}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>

      <SideDrawer
        open={Boolean(selectedProject && selectedFile)}
        onClose={() => setSelectedFile(null)}
        title=""
      >
        {selectedProject && selectedFile && (
          <GitLabFilePreview
            projectId={selectedProject.id}
            filePath={selectedFile.path}
            webUrl={
              selectedProject.web_url
                ? `${selectedProject.web_url}/-/blob/HEAD/${selectedFile.path}`
                : undefined
            }
          />
        )}
      </SideDrawer>
    </MainPanelContent>
  );
}
